"use client";

/**
 * The visitor hero: the headline, one line on what the product does and
 * the search box, over the city map. Nothing numeric up here (owner,
 * 2026-10-26: "the hero card section is overcrowded, and most
 * importantly way more overcrowded on mobile"). The rotating card of
 * real analyses that filled the right column (fair value, its range, the
 * confidence, the verdict, the district average) is gone: the
 * recent-valuations row below shows the same real output, and the
 * market's figures are one line in the facts block
 * (components/home/VisitorMarketNow). The calculator line and the map
 * button that rode under the box left with it: the box takes words as
 * well as links, and both destinations live in the nav and the tools row.
 *
 * On phones the empty box carries a "Yapışdırın" button (UX plan 3.1,
 * 2026-10-29): the link there comes from another app, and the round
 * clipboard icon alone was not read as paste. From sm up it stays the
 * icon. The phone placeholder is the short one so it is not cut off
 * under the button.
 *
 *   +--------------------------------------------------------------+
 *   |  Every apartment and house listing in Baku,        . map .   |
 *   |  each with its fair price                        .  of Baki . |
 *   |                                                   .         . |
 *   |  Baku's listing sites in one search. ...            .  .  .   |
 *   |  [ Search: Yasamal 2 rooms, or a listing link ] [ Search ]    |
 *   +--------------------------------------------------------------+
 *
 * On phones the headline, the line and the box are the first screen's
 * only content, the map faint underneath, and the next block's top edge
 * shows under the box.
 */

import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { looksLikeLink } from "@/lib/listing-url";
import { useT } from "@/lib/locale-context";
import PasteFromClipboard from "@/components/PasteFromClipboard";
import UrlPreviewCard from "@/components/UrlPreviewCard";
import type { Intent } from "@/lib/use-personal";
import type { TranslationKey } from "@/lib/i18n";

/** The hero map's vertical mask: in at the nav, then a long eased
 *  dissolve into the page background at the bottom (see the backdrop
 *  comments in HomeHeroSplit). Desktop keeps the map full to 56%; the
 *  phone map is quieter under the copy and full from 64%. */
const HERO_FADE_WIDE =
  "linear-gradient(to bottom, transparent, black 12%, black 56%, rgba(0,0,0,0.7) 70%, rgba(0,0,0,0.38) 82%, rgba(0,0,0,0.14) 92%, transparent)";
const HERO_FADE_PHONE =
  "linear-gradient(to bottom, transparent, rgba(0,0,0,0.4) 12%, rgba(0,0,0,0.4) 52%, black 64%, black 70%, rgba(0,0,0,0.62) 80%, rgba(0,0,0,0.3) 90%, rgba(0,0,0,0.1) 96%, transparent)";

interface Props {
  /** Two-way bound URL string the parent owns. The parent submits
   *  the form, tracks "via" attribution, etc. - HeroSplit only owns
   *  the input UI. */
  url: string;
  onUrlChange: (next: string) => void;
  onSubmit: () => void;
  /** A search is being parsed; the button waits. */
  busy?: boolean;
  /** Fires when the user pastes via the clipboard button - parent
   *  submits immediately (share-target-style UX). */
  onPaste: (url: string) => void;
  /** Under sm: the short placeholder, which fits beside the paste
   *  button. Server HTML and the first paint use the long one. */
  isMobile?: boolean;
  /** Skip the URL pre-fill loop when the parent already has a
   *  value to write (anonymous demo) - lets us reserve the preview
   *  card's vertical slot from the first paint so the layout
   *  doesn't shift when the prefill resolves. */
  prefillPending: boolean;
  /** What the visitor is here for (lib/use-personal): the subtitle
   *  speaks to it. The headline and the input stay the same. */
  intent?: Intent | null;
}

type MapModule = typeof import("@/components/home/HeroDistrictMap");
let mapModule: MapModule | null = null;
const loadMap = () => import("@/components/home/HeroDistrictMap").then((m) => (mapModule = m));

/**
 * The hero map backdrop, drawn in the browser only and only for the
 * layout on screen (`wide` = md and up). Server-rendered, the two copies
 * (desktop + phone) were ~290 KB of SVG markup at the top of the home
 * page's HTML, ahead of the copy, and ~2,400 nodes to hydrate; it is
 * decorative (aria-hidden), so it fades in right after hydration. The
 * map and its compiled outlines (~46 KB of code) are a chunk of their
 * own that this fetches then, out of the page's first load (2026-10-19).
 */
function HeroBackdrop({ wide }: { wide: boolean }) {
  const [show, setShow] = useState(false);
  const [visible, setVisible] = useState(false);
  const [map, setMap] = useState<MapModule | null>(mapModule);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    let id = 0;
    let alive = true;
    const apply = () => {
      const on = mq.matches === wide;
      setShow(on);
      if (!on) return;
      loadMap().then((m) => {
        if (!alive) return;
        setMap(m);
        id = requestAnimationFrame(() => setVisible(true));
      }, () => { /* decorative: no map is fine */ });
    };
    apply();
    mq.addEventListener("change", apply);
    return () => {
      alive = false;
      cancelAnimationFrame(id);
      mq.removeEventListener("change", apply);
    };
  }, [wide]);
  if (!show || !map) return null;
  const HeroDistrictMap = map.default;
  return (
    <div
      className="h-full w-full motion-safe:transition-opacity motion-safe:duration-500 motion-safe:ease-out"
      style={{ opacity: visible ? 1 : 0 }}
    >
      <HeroDistrictMap />
    </div>
  );
}

export default function HomeHeroSplit({
  url,
  onUrlChange,
  onSubmit,
  busy = false,
  onPaste,
  isMobile = false,
  prefillPending,
  intent = null,
}: Props) {
  const t = useT();
  // Held so the X-clear button can wipe the value AND restore focus
  // to the input in a single tap - users who want to paste a new URL
  // shouldn't have to tap the field again afterwards.
  const inputRef = useRef<HTMLInputElement>(null);
  const trimmed = url.trim();
  const hasInput = trimmed.length > 0;
  // Words search the listings; a link is valued, and the button says so
  // (owner, 2026-10-29: "the text should be changed to 'evaluate'").
  // Every link is accepted: a listing site is valued, any other page or
  // post is read by the intake on /analyze (owner, 2026-10-16).
  const isLink = hasInput && looksLikeLink(trimmed);

  return (
    // Clean text-forward hero (no background photo). Colours are
    // theme-aware via bare gray classes (the .dark var-swap flips them).
    //
    // `relative isolate overflow-hidden` hosts the map layers below:
    // `isolate` opens a stacking context so the -z-10 layer sits behind
    // the hero content but never behind the page background;
    // `overflow-hidden` clips the map at the section's edges.
    //
    // Phones: the content flows, so the next block's top edge shows
    // under the search box (the eye sees there is more); nothing but
    // the headline, the line and the box stands above it.
    <section className="relative isolate overflow-hidden pt-10 pb-12 sm:pt-16 sm:pb-14 lg:pt-20">
      {/* Non-image background texture (de-slop board 5a/8a): Bakı's
          REAL district linework as inline SVG - the product's own
          geography is the one sanctioned hero texture. It is the hero's
          visual now that the copy stands alone: faint under the copy on
          the left, full on the right. Hidden under md - phones keep
          the flat surface. */}
      {/* FULL-BLEED: the map owns the whole hero. Under the copy
          it stays present but subdued (partial-opacity mask,
          never fully erased); it dissolves softly at the nav and the
          next section (nested vertical mask intersects). The bottom
          dissolve is long and eased (the hero's last 44%, HERO_FADE_WIDE):
          the old 14% ramp was invisible on the dark paper, where the
          map's bay is a few points off the background, and read as a
          band in light mode, where the bay sits ~70 RGB points under the
          page (owner, 2026-10-25). A mask reveals whatever is behind the
          map, the page background token, so one ramp serves both themes. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 hidden md:block"
        style={{
          maskImage:
            "linear-gradient(to right, rgba(0,0,0,0.1), rgba(0,0,0,0.22) 38%, black 58%)",
          WebkitMaskImage:
            "linear-gradient(to right, rgba(0,0,0,0.1), rgba(0,0,0,0.22) 38%, black 58%)",
        }}
      >
        <div
          className="h-full w-full"
          style={{ maskImage: HERO_FADE_WIDE, WebkitMaskImage: HERO_FADE_WIDE }}
        >
          <HeroBackdrop wide />
        </div>
      </div>

      {/* Mobile: same map, quieter - the copy stacks on top of it, so
          it runs at reduced opacity with a vertical-only fade. The
          board's original hide-on-mobile call predates the heat layer;
          the owner wants the data visible on phones too. */}
      <div
        aria-hidden="true"
        // 45% reads fine over the light page; over the dark one the map's
        // own near-black fills at 45% left almost nothing visible on a
        // phone (owner, 2026-09-22: "the hero background is barely
        // visible"), so dark mode gets more of it.
        className="pointer-events-none absolute inset-0 -z-10 opacity-45 dark:opacity-80 md:hidden"
        // Quiet under the stacked copy (top half), fuller below it,
        // dissolving over the last 30% before the next section (the same
        // long, eased ramp as the desktop mask, for the same reason).
        style={{ maskImage: HERO_FADE_PHONE, WebkitMaskImage: HERO_FADE_PHONE }}
      >
        <HeroBackdrop wide={false} />
      </div>
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 lg:flex lg:min-h-[440px] lg:items-center">
        {/* The copy and the box: one column, left on desktop beside the
            map, centred below it. Staggered mount entrance (enter-rise
            for the h1 and its line so they count as the LCP; globals.css)
            - the 50ms halo moment. */}
        <div className="mx-auto max-w-2xl text-center lg:mx-0 lg:text-left">
          <h1 className="enter-rise text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight leading-[1.05]">
            {t("hero.title.1")}{" "}
            {/* One colour; the weight carries the emphasis. The second
                half used to be indigo, a two-colour headline (owner,
                2026-10-18). */}
            <span className="font-extrabold">{t("hero.title.2")}</span>
          </h1>

          <p
            className="enter-rise mt-5 text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl mx-auto lg:mx-0"
            style={{ animationDelay: "90ms" }}
          >
            {t(intent ? (`hero.subtitle.${intent}` as TranslationKey) : "hero.subtitle")}
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              // An empty box puts the cursor where the link goes instead of
              // showing a dead button (wave 3).
              if (!hasInput) { inputRef.current?.focus(); return; }
              onSubmit();
            }}
            className="mt-7 max-w-xl mx-auto lg:mx-0"
          >
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <div className="relative flex-1">
                <input
                  ref={inputRef}
                  type="text"
                  value={url}
                  onChange={(e) => onUrlChange(e.target.value)}
                  placeholder={t(isMobile ? "hero.inputplaceholder.short" : "hero.inputplaceholder")}
                  // The empty box holds the worded paste button on phones.
                  className={`w-full py-3.5 sm:py-4 pl-5 ${trimmed ? "pr-12" : "pr-[7.5rem] sm:pr-12"} border border-gray-200 rounded-xl text-base bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-colors`}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  {trimmed ? (
                    <button
                      type="button"
                      onClick={() => {
                        onUrlChange("");
                        // Re-focus so the user can paste/type the
                        // next URL without an extra tap.
                        inputRef.current?.focus();
                      }}
                      aria-label={t("paste.clear")}
                      title={t("paste.clear")}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      <X className="h-4 w-4 text-gray-500" />
                    </button>
                  ) : (
                    <PasteFromClipboard onPaste={onPaste} labelOnPhone />
                  )}
                </div>
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full sm:w-auto px-5 sm:px-7 py-3.5 sm:py-4 bg-indigo-600 text-white rounded-xl font-semibold text-base hover:bg-indigo-700 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors sm:shrink-0 shadow-sm"
              >
                {isLink ? t("home.search.evaluate") : t("hero.inputbutton.search")}
              </button>
            </div>
            {/* Live preview of a pasted link, under the box. Renders an
                eager skeleton when ``prefillPending`` is true so the
                layout reserves the card's slot from the first paint. */}
            <UrlPreviewCard
              url={url}
              variant="form"
              className="mt-3"
              eagerSkeleton={prefillPending}
              onActivate={hasInput ? onSubmit : undefined}
            />
          </form>
        </div>
      </div>
    </section>
  );
}
